import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, LockKeyhole, ShieldCheck } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'

export function LoginPage() {
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!username.trim() || !password) return
    setSubmitting(true)
    setError(null)
    try {
      await login(username, password)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to sign in. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-pine-950 px-4 py-10 sm:px-6">
      <div className="absolute inset-0 opacity-40" aria-hidden="true">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-pine-700 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-ember-700/40 blur-3xl" />
      </div>

      <section className="relative grid w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-modal lg:grid-cols-[1.05fr_.95fr]">
        <div className="hidden flex-col justify-between bg-pine-900 p-10 lg:flex">
          <img src="/termovista-logo.png" alt="TermoVista" className="h-auto w-64" />
          <div>
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-ember-400 ring-1 ring-white/10">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="max-w-sm text-3xl font-bold tracking-tight text-white">Your operations workspace, kept private.</h1>
            <p className="mt-4 max-w-sm text-sm leading-6 text-pine-200/75">Sign in to manage TermoVista customers, opportunities, quotations and projects.</p>
          </div>
          <p className="text-xs text-pine-300/60">Restricted to authorised TermoVista users.</p>
        </div>

        <div className="px-6 py-9 sm:px-10 sm:py-12">
          <img src="/termovista-icon.png" alt="" className="mb-8 h-12 w-12 rounded-xl lg:hidden" />
          <div className="mb-8">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-pine-50 text-pine-700">
              <LockKeyhole className="h-5 w-5" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-pine-950">Welcome back</h2>
            <p className="mt-2 text-sm text-slate-500">Enter your TermoVista credentials to continue.</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="field-label" htmlFor="login-username">Username</label>
              <input
                id="login-username"
                className="field-input"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                autoFocus
                disabled={submitting}
                required
              />
            </div>
            <div>
              <label className="field-label" htmlFor="login-password">Password</label>
              <div className="relative">
                <input
                  id="login-password"
                  className="field-input pr-11"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-pine-700"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700" role="alert">{error}</div>
            )}

            <button className="btn-primary w-full" type="submit" disabled={submitting || !username.trim() || !password}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-8 text-center text-xs leading-5 text-slate-400">Your session uses a signed, HTTP-only cookie and expires automatically.</p>
        </div>
      </section>
    </main>
  )
}
